import { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';

const PhoneLogin = () => {
  const [step, setStep] = useState(1); // 1: Enter phone, 2: Enter verification code
  const [formData, setFormData] = useState({
    phone: '',
    name: '',
    code: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { sendPhoneVerificationCode, verifyPhoneWithCode, isAuthenticated, error, clearError } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
    
    // Clear any existing errors
    return () => {
      clearError();
    };
  }, [isAuthenticated, navigate, clearError]);
  
  const { phone, name, code } = formData;
  
  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error for this field
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: '' });
    }
  };
  
  const validatePhoneForm = () => {
    const errors = {};
    
    if (!phone) {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{9,14}$/.test(phone)) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    if (!name) {
      errors.name = 'Name is required for new users';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const validateCodeForm = () => {
    const errors = {};
    
    if (!code) {
      errors.code = 'Verification code is required';
    } else if (!/^\d{6}$/.test(code)) {
      errors.code = 'Verification code must be 6 digits';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSendCode = async e => {
    e.preventDefault();
    
    if (!validatePhoneForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    const result = await sendPhoneVerificationCode(phone, name);
    
    setIsSubmitting(false);
    
    if (result.success) {
      setStep(2);
    }
  };
  
  const handleVerifyCode = async e => {
    e.preventDefault();
    
    if (!validateCodeForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    const result = await verifyPhoneWithCode(phone, code);
    
    setIsSubmitting(false);
    
    if (result.success) {
      navigate('/');
    }
  };
  
  const handleResendCode = async () => {
    setIsSubmitting(true);
    
    await sendPhoneVerificationCode(phone, name);
    
    setIsSubmitting(false);
  };
  
  return (
    <div className="max-w-md mx-auto my-8">
      <div className="neumorph p-8 rounded-lg">
        <h2 className="text-2xl font-bold mb-6 text-center">Phone Login</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {step === 1 ? (
          <form onSubmit={handleSendCode}>
            <div className="mb-4">
              <label htmlFor="phone" className="block text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={phone}
                onChange={handleChange}
                className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                  formErrors.phone ? 'border border-red-500' : ''
                }`}
                placeholder="Enter your phone number (e.g. +1234567890)"
              />
              {formErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
              )}
            </div>
            
            <div className="mb-6">
              <label htmlFor="name" className="block text-gray-700 mb-2">Name (for new users)</label>
              <input
                type="text"
                id="name"
                name="name"
                value={name}
                onChange={handleChange}
                className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                  formErrors.name ? 'border border-red-500' : ''
                }`}
                placeholder="Enter your name"
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>
            
            <button
              type="submit"
              className="neumorph-btn w-full py-2 px-4 rounded-lg text-gray-800 font-medium"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending Code...' : 'Send Verification Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode}>
            <div className="mb-6">
              <label htmlFor="code" className="block text-gray-700 mb-2">Verification Code</label>
              <input
                type="text"
                id="code"
                name="code"
                value={code}
                onChange={handleChange}
                className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                  formErrors.code ? 'border border-red-500' : ''
                }`}
                placeholder="Enter 6-digit verification code"
                maxLength={6}
              />
              {formErrors.code && (
                <p className="text-red-500 text-sm mt-1">{formErrors.code}</p>
              )}
            </div>
            
            <button
              type="submit"
              className="neumorph-btn w-full py-2 px-4 rounded-lg text-gray-800 font-medium mb-4"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Verifying...' : 'Verify Code'}
            </button>
            
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-blue-600 hover:underline"
              >
                Change Phone Number
              </button>
              
              <button
                type="button"
                onClick={handleResendCode}
                className="text-blue-600 hover:underline"
                disabled={isSubmitting}
              >
                Resend Code
              </button>
            </div>
          </form>
        )}
        
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Want to use email instead?{' '}
            <Link to="/login" className="text-blue-600 hover:underline">
              Login with Email
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PhoneLogin;
